// badgeLogic.js
// Direct port of BadgeLogic.swift — computes all badges client-side from stats.
// Badge IDs, titles, descriptions, XP values and unlock conditions are identical
// to the iOS app so badge state is always consistent across platforms.

// ─── Category definitions ─────────────────────────────────────────────────────
// Mirrors BadgeCategory enum in BadgeProgress.swift

export const BADGE_CATEGORIES = [
  { id: "rosterable",   label: "ROSTERABLE"     },
  { id: "chemistry",    label: "CHEMISTRY"       },
  { id: "drafts",       label: "DRAFTS"          },
  { id: "streaks",      label: "STREAKS"         },
  { id: "dynasty",      label: "DYNASTY"         },
  { id: "versus",       label: "ROSTER BATTLES"  },
  { id: "knockout",     label: "KNOCKOUT"        },
  { id: "leaderboard",  label: "LEADERBOARD"     },
  { id: "achievements", label: "ACHIEVEMENTS"    },
];

// ─── Helper: parse Swift [Sport:Value] flat arrays ────────────────────────────
function parseDict(arr) {
  if (!arr || !Array.isArray(arr)) return {};
  const r = {};
  for (let i = 0; i < arr.length - 1; i += 2) r[arr[i]] = arr[i + 1];
  return r;
}

// ─── Badge factory helpers ────────────────────────────────────────────────────

function badge(badgeID, title, description, current, required, category, xpValue) {
  const isUnlocked = current >= required;
  return {
    badgeID, title, description, isUnlocked, category, xpValue,
    currentValue: current, requiredValue: required,
    progressFraction: Math.min(1, current / Math.max(1, required)),
  };
}

function rankBadge(badgeID, title, description, bestRank, threshold, category, xpValue) {
  const isUnlocked = bestRank !== null && bestRank <= threshold;
  const progress = bestRank === null ? 0 : Math.max(0, threshold - bestRank + 1);
  return {
    badgeID, title, description, isUnlocked, category, xpValue,
    currentValue: progress, requiredValue: threshold,
    progressFraction: Math.min(1, progress / Math.max(1, threshold)),
  };
}

// ─── Main compute function ────────────────────────────────────────────────────
// Mirrors computeAllBadges() in BadgeLogic.swift exactly.
// Pass the raw parsed stats object from badgeStats Firestore document.

export function computeAllBadges(stats) {
  // Parse all Swift dictionaries
  const bestRating     = parseDict(stats.bestRatingBySport);
  const bestStreak     = parseDict(stats.bestStreakBySport);
  const bestDynasty    = parseDict(stats.bestDynastyRatingBySport);
  const totalDrafts    = parseDict(stats.totalDraftsBySport);
  const bestDailyDraft = parseDict(stats.bestDailyDraftRankBySport);
  const bestMoDraft    = parseDict(stats.bestMonthlyDraftRankBySport);
  const awardedIDs     = Array.isArray(stats.awardedBadgeIDs) ? stats.awardedBadgeIDs : [];

  const SPORTS = ["Football", "Baseball", "Basketball", "Hockey", "Soccer"];
  const total  = SPORTS.length;

  let all = [];

  // 1. ROSTERABLE
  const drafts = stats.totalDraftsAllTime || 0;
  const days   = stats.bestConsecutiveDays || 0;
  all.push(
    badge("volume_1",         "FIRST PICK",           "Complete your first draft",               drafts, 1,   "rosterable", 250),
    badge("volume_dialed_in", "DIALED IN",            "Complete 10 drafts in a single day",      stats.hasTenDraftsInOneDay ? 1 : 0, 1, "rosterable", 2000),
    badge("consecutive_10",   "CONSISTENCY IS KEY",   "10 consecutive days drafted",             days,   10,  "rosterable", 5000),
    badge("consecutive_25",   "ON A ROLL",            "25 consecutive days drafted",             days,   25,  "rosterable", 7500),
    badge("consecutive_50",   "HOT STREAK",           "50 consecutive days drafted",             days,   50,  "rosterable", 10000),
    badge("consecutive_100",  "LOCKED IN",            "100 consecutive days drafted",            days,   100, "rosterable", 20000),
    badge("consecutive_300",  "IRON MAN",             "300 consecutive days drafted",            days,   300, "rosterable", 50000),
    badge("consecutive_500",  "LEGEND",               "500 consecutive days drafted",            days,   500, "rosterable", 100000),
    badge("volume_50",        "GETTING WARMED UP",    "Complete 50 drafts",                      drafts, 50,  "rosterable", 500),
    badge("volume_100",       "CENTURION",            "Complete 100 drafts",                     drafts, 100, "rosterable", 1000),
    badge("volume_250",       "DEDICATED",            "Complete 250 drafts",                     drafts, 250, "rosterable", 2500),
    badge("volume_500",       "COMMITTED",            "Complete 500 drafts",                     drafts, 500, "rosterable", 5000),
    badge("volume_1000",      "THOUSAND DEEP",        "Complete 1000 drafts",                    drafts, 1000,"rosterable", 10000),
    badge("volume_2500",      "THE GRIND",            "Complete 2500 drafts",                    drafts, 2500,"rosterable", 25000),
    badge("volume_5000",      "LIFELONG BALLER",      "Complete 5000 drafts",                    drafts, 5000,"rosterable", 50000),
  );

  // 2. CHEMISTRY
  const chem = stats.bestChemistryBonus || 0;
  all.push(
    badge("chemistry_25",  "EXPERIMENTING", "25+ chemistry bonus",  chem, 25,  "chemistry", 500),
    badge("chemistry_50",  "SYNERGY",       "50+ chemistry bonus",  chem, 50,  "chemistry", 1000),
    badge("chemistry_100", "LET 'EM COOK",  "75+ chemistry bonus",  chem, 75,  "chemistry", 2500),
    badge("chemistry_150", "IN THE LAB",    "100+ chemistry bonus", chem, 100, "chemistry", 5000),
    badge("chemistry_200", "MAD SCIENTIST", "125+ chemistry bonus", chem, 125, "chemistry", 10000),
  );

  // 3. DRAFTS — All-sports thresholds
  const draftThresholds = [750, 800, 850, 900, 950];
  const draftTitles     = ["MULTI-SPORT ATHLETE", "JACK OF ALL TRADES", "SWISS ARMY KNIFE", "SPORTS SAVANT", "5 TOOL PLAYER"];
  const draftXPs        = [500, 1000, 2500, 5000, 10000];
  draftThresholds.forEach((threshold, i) => {
    const reached = SPORTS.filter(s => (bestRating[s] || 0) >= threshold).length;
    all.push({
      badgeID: `allsports_${threshold}`, title: draftTitles[i],
      description: `${threshold}+ score in all 5 sports`,
      isUnlocked: reached >= total, category: "drafts", xpValue: draftXPs[i],
      currentValue: reached, requiredValue: total,
      progressFraction: Math.min(1, reached / total),
    });
  });

  const maxRating = Math.max(0, ...Object.values(bestRating).filter(Number.isFinite));

  // Draft tale badges
  all.push(
    badge("tales_bold_strategy",       "BOLD STRATEGY COTTON",    "Draft a team rating under 750",                    stats.hasCompletedUnder700Draft ? 1 : 0, 1, "drafts", 5000),
    badge("tales_gentlemans_sweep",    "GENTLEMAN'S SWEEP",       "Score 875+ in all 5 sports in a single day",       stats.hasGentlemansSweep ? 1 : 0, 1, "drafts", 10000),
    badge("tales_clean_sweep",         "CLEAN SWEEP",             "Score 925+ in all 5 sports in a single day",       stats.hasCleanSweep ? 1 : 0, 1, "drafts", 25000),
    badge("tales_999_club",            "999 CLUB",                "Achieve a perfect 999 team rating",                maxRating, 999, "drafts", 50000),
    badge("tales_diamond_in_the_rough","DIAMOND IN THE ROUGH",    "Score 950+ rostering a player rated 75 or below",  stats.hasDiamondInTheRough ? 1 : 0, 1, "drafts", 5000),
    badge("tales_built_different",     "BUILT DIFFERENT",         "Score 900+ with zero chemistry bonus",             stats.hasBuiltDifferent ? 1 : 0, 1, "drafts", 5000),
    badge("tales_moneyball",           "MONEYBALL",               "Score 850+ with no player rated above 82",         stats.hasMoneyball ? 1 : 0, 1, "drafts", 10000),
    badge("tales_all_timer",           "ALL-TIMER",               "Draft a full team of players rated 90 or above",  stats.hasAllTimer ? 1 : 0, 1, "drafts", 5000),
    badge("tales_playing_favorites",   "GIGA STACK",              "Draft 5+ players from the same team",              stats.hasPlayedFavorites ? 1 : 0, 1, "drafts", 5000),
    badge("tales_gangs_all_here",      "GANG'S ALL HERE",         "Draft 3 players from the same team & season",      stats.hasGangsAllHere ? 1 : 0, 1, "drafts", 20000),
    badge("tales_new_millennium",      "NEW MILLENNIUM",          "Draft a full team of players from the 2000s",      stats.hasDraftedAllFrom2000s ? 1 : 0, 1, "drafts", 5000),
    badge("tales_glory_days",          "GLORY DAYS",              "Draft a full team of players from the 2010s",      stats.hasDraftedAllFrom2010s ? 1 : 0, 1, "drafts", 3000),
    badge("tales_new_blood",           "NEW BLOOD",               "Draft a full team of players from the 2020s",      stats.hasDraftedAllFrom2020s ? 1 : 0, 1, "drafts", 5000),
    badge("tales_time_capsule",        "TIME CAPSULE",            "Draft a full team from the exact same year",       stats.hasTimeCapsule ? 1 : 0, 1, "drafts", 50000),
    badge("tales_back_stack",          "BACK STACK",              "Final 3 players drafted all from the same team",   stats.hasBackStack ? 1 : 0, 1, "drafts", 10000),
    badge("tales_zero_qb",             "ZERO QB",                 "QB rated under 75 with a team rating over 900",    stats.hasZeroQB ? 1 : 0, 1, "drafts", 5000),
    badge("tales_zero_rb",             "ZERO RB",                 "RB rated under 75 with a team rating over 900",    stats.hasZeroRB ? 1 : 0, 1, "drafts", 5000),
    badge("tales_zero_wr",             "ZERO WR",                 "WRs rated under 75 with a team rating over 900",   stats.hasZeroWR ? 1 : 0, 1, "drafts", 10000),
    badge("tales_zero_te",             "ZERO TE",                 "TE rated under 75 with a team rating over 900",    stats.hasZeroTE ? 1 : 0, 1, "drafts", 5000),
    badge("tales_slappy",              "SLAPPY",                  "You know what you did",                            stats.hasSlappy ? 1 : 0, 1, "drafts", 5000),
    // Position stacking
    badge("chem_pitch_and_catch",      "PITCH AND CATCH",         "QB and WR or TE from the same team and year",      stats.hasPitchAndCatch ? 1 : 0, 1, "drafts", 15000),
    badge("chem_ground_and_pound",     "GROUND AND POUND",        "RB and FB from the same team",                     stats.hasGroundAndPound ? 1 : 0, 1, "drafts", 15000),
    badge("chem_ball_hawks",           "BALL HAWKS",              "Entire secondary from the same team",              stats.hasBallHawks ? 1 : 0, 1, "drafts", 25000),
    badge("chem_battery",              "THE BATTERY",             "SP and C from the same team and year",             stats.hasTheBattery ? 1 : 0, 1, "drafts", 15000),
    badge("chem_around_the_horn",      "AROUND THE HORN",         "Entire infield from the same team",                stats.hasAroundTheHorn ? 1 : 0, 1, "drafts", 50000),
    badge("chem_can_of_corn",          "CAN OF CORN",             "Entire outfield from the same team",               stats.hasCanOfCorn ? 1 : 0, 1, "drafts", 25000),
    badge("chem_backcourt_tandem",     "BACKCOURT TANDEM",        "Starting PG and SG from the same team and year",   stats.hasBackcourtTandem ? 1 : 0, 1, "drafts", 15000),
    badge("chem_big_fellas",           "BIG FELLAS",              "Starting PF and C from the same team and year",    stats.hasBigFellas ? 1 : 0, 1, "drafts", 15000),
    badge("chem_bench_mob",            "BENCH MOB",               "2 bench players from the same team and year",      stats.hasBenchMob ? 1 : 0, 1, "drafts", 5000),
    badge("chem_top_line",             "TOP LINE",                "C, LW, and RW all from the same team",             stats.hasTopLine ? 1 : 0, 1, "drafts", 25000),
    badge("chem_top_pair",             "TOP PAIR",                "LD and RD from the same team and year",            stats.hasTopPair ? 1 : 0, 1, "drafts", 15000),
    badge("chem_line_change",          "LINE CHANGE",             "Second line LW, C, and RW all from the same team", stats.hasLineChange ? 1 : 0, 1, "drafts", 5000),
    badge("chem_jogo_bonito",          "JOGO BONITO",             "All 3 forwards from the same team",                stats.hasJogoBonito ? 1 : 0, 1, "drafts", 25000),
    badge("chem_pretty_mid",           "PRETTY MID",              "All 3 midfielders from the same team",             stats.hasPrettyMid ? 1 : 0, 1, "drafts", 25000),
    badge("chem_first_line_of_defense","FIRST LINE OF DEFENSE",   "All 4 defenders from the same team",               stats.hasFirstLineOfDefense ? 1 : 0, 1, "drafts", 50000),
  );

  // 4. STREAKS — use bestStreakAllTime from stats
  const bestStreakVal = stats.bestStreakAllTime || 0;
  const totalStreaks  = stats.totalStreaksAllTime || 0;
  all.push(
    badge("streak_100vol",       "STREAK MACHINE",        "Play 100 total Streak games",                  totalStreaks, 100, "streaks", 2500),
    badge("streak_10",           "HEAT CHECK",            "5+ Streak in any sport",                       bestStreakVal, 5,  "streaks", 2500),
    badge("streak_25",           "ON FIRE",               "10+ Streak in any sport",                      bestStreakVal, 10, "streaks", 5000),
    badge("streak_50",           "CAN'T MISS",            "20+ Streak in any sport",                      bestStreakVal, 20, "streaks", 10000),
    badge("streak_75",           "LEGENDARY RUN",         "30+ Streak in any sport",                      bestStreakVal, 30, "streaks", 25000),
    badge("streak_100",          "UNSTOPPABLE",           "50+ Streak in any sport",                      bestStreakVal, 50, "streaks", 50000),
    badge("streak_unbreakable",  "UNBREAKABLE RECORD",    "75+ Streak in any sport",                      bestStreakVal, 75, "streaks", 75000),
    badge("streak_in_the_zone",  "IN THE ZONE",           "Extend a streak with a player rated under 75", stats.hasStreakExtendedWithUnder75 ? 1 : 0, 1, "streaks", 5000),
    badge("streak_cold_blooded", "COLD BLOODED",          "10+ Streak without using any power-ups",        stats.hasColdBlooded ? 1 : 0, 1, "streaks", 10000),
    badge("streak_speed_demon",  "SPEED DEMON",           "Streak of 20+ with avg pick time under 4s",    stats.hasSpeedDemon ? 1 : 0, 1, "streaks", 25000),
  );

  // 5. DYNASTY
  const dynastyWins   = stats.totalDynastyWins || 0;
  const founded       = Array.isArray(stats.dynastyFoundedSports) ? stats.dynastyFoundedSports.length : 0;
  const bestDynRating = Math.max(0, ...Object.values(bestDynasty).filter(Number.isFinite));

  const allSportsThresholds = [700, 750, 800, 850, 900];
  const allSportsTitles     = ["BUILDING A FOUNDATION", "ACROSS THE BOARD", "CHAMPION PEDIGREE", "GOLD STANDARD", "TOTAL DOMINATION"];
  const allSportsXP         = [2500, 5000, 10000, 15000, 25000];

  all.push(
    badge("dynasty_founder",      "DYNASTY FOUNDER",   "Complete your first dynasty founding draft", founded >= 1 ? 1 : 0, 1, "dynasty", 1000),
    badge("dynasty_single_800",   "BUILDING A WINNER", "800+ on any Dynasty roster",  bestDynRating, 800, "dynasty", 5000),
    badge("dynasty_single_850",   "BLUE BLOOD",        "850+ on any Dynasty roster",  bestDynRating, 850, "dynasty", 10000),
    badge("dynasty_single_900",   "BANNER SEASON",     "900+ on any Dynasty roster",  bestDynRating, 900, "dynasty", 15000),
    badge("dynasty_single_950",   "HISTORIC RUN",      "950+ on any Dynasty roster",  bestDynRating, 950, "dynasty", 25000),
    badge("dynasty_single_999",   "UNTOUCHABLE",       "999 on any Dynasty roster",   bestDynRating, 999, "dynasty", 50000),
  );

  allSportsThresholds.forEach((threshold, i) => {
    const reached = SPORTS.filter(s => (bestDynasty[s] || 0) >= threshold).length;
    all.push({
      badgeID: `dynasty_all_${threshold}`, title: allSportsTitles[i],
      description: `${threshold}+ Dynasty rating in all 5 sports`,
      isUnlocked: reached >= total, category: "dynasty", xpValue: allSportsXP[i],
      currentValue: reached, requiredValue: total,
      progressFraction: Math.min(1, reached / total),
    });
  });

  all.push(
    badge("dynasty_veteran",           "DYNASTY VETERAN",    "Win 10 dynasty challenges",                          dynastyWins, 10,  "dynasty", 1500),
    badge("dynasty_legend",            "DYNASTY LEGEND",     "Win 50 dynasty challenges",                          dynastyWins, 50,  "dynasty", 5000),
    badge("dynasty_goat",              "DYNASTY GOAT",       "Win 100 dynasty challenges",                         dynastyWins, 100, "dynasty", 15000),
    badge("dynasty_challenge_accepted","CHALLENGE ACCEPTED",  "Win all 6 challenge levels for a sport in one day", stats.hasCompletedAllDailyChallenges ? 1 : 0, 1, "dynasty", 5000),
    badge("dynasty_homegrown",         "HOME GROWN",          "Build a dynasty roster entirely from one city",      stats.hasDynastyHomegrown ? 1 : 0, 1, "dynasty", 50000),
    badge("dynasty_no_shortcuts",      "NO SHORTCUTS",        "Dynasty founding draft under 700 rating",            stats.hasDynastyUnder700 ? 1 : 0, 1, "dynasty", 2000),
    badge("dynasty_rebuild",           "THE REBUILD",         "Use the dynasty reset feature",                      stats.hasDynastyReset ? 1 : 0, 1, "dynasty", 2500),
  );

  // 6. VERSUS (Roster Battles)
  const wins       = stats.totalPickUpWins  || 0;
  const streak_v   = stats.bestPickUpWinStreak || 0;
  const played     = (stats.totalPickUpWins || 0) + (stats.totalPickUpLosses || 0);
  all.push(
    badge("versus_first_blood",   "FIRST BLOOD",   "Win your first Pick Up Draft",          wins,    1,   "versus", 500),
    badge("versus_on_the_come_up","ON THE COME-UP", "Win 5 Pick Up Drafts",                 wins,    5,   "versus", 2500),
    badge("versus_contender",     "CONTENDER",     "Win 10 Pick Up Drafts",                 wins,    10,  "versus", 5000),
    badge("versus_veteran",       "VETERAN",       "Win 25 Pick Up Drafts",                 wins,    25,  "versus", 10000),
    badge("versus_elite",         "ELITE",         "Win 50 Pick Up Drafts",                 wins,    50,  "versus", 25000),
    badge("versus_goat",          "G.O.A.T.",      "Win 100 Pick Up Drafts",                wins,    100, "versus", 50000),
    badge("versus_hot_hand",      "SWEEP",         "Win 3 Pick Up Drafts in a row",         streak_v, 3,  "versus", 2500),
    badge("versus_undefeated",    "HEATING UP",    "Win 5 Pick Up Drafts in a row",         streak_v, 5,  "versus", 5000),
    badge("versus_no_mercy",      "NO MERCY",      "Win 10 Pick Up Drafts in a row",        streak_v, 10, "versus", 15000),
    badge("versus_seasoned",      "SEASONED",      "Play 25 Pick Up Drafts",                played,  25,  "versus", 5000),
    badge("versus_grinder",       "GRINDER",       "Play 100 Pick Up Drafts",               played,  100, "versus", 15000),
  );

  // 7. KNOCKOUT
  const koWins      = stats.knockoutWins || 0;
  const koBestRun   = stats.knockoutBestRoundsAdvanced || 0;
  const koTotal     = stats.knockoutTotalRoundsAdvanced || 0;
  all.push(
    badge("knockout_survivor",    "SURVIVOR",    "Advance past Day 1 in a Knockout Pool",         koTotal, 1,  "knockout", 1000),
    badge("knockout_deep_run",    "DEEP RUN",    "Reach Day 5 in a Knockout Pool",                koBestRun, 4, "knockout", 5000),
    badge("knockout_finals",      "THE FINALS",  "Reach the final 1v1 round of a Knockout Pool",  koBestRun, 6, "knockout", 15000),
    badge("knockout_champion",    "CHAMPION",    "Win your first Knockout Pool",                   koWins,  1,  "knockout", 25000),
    badge("knockout_dynasty",     "DYNASTY",     "Win 3 Knockout Pools",                           koWins,  3,  "knockout", 50000),
    badge("knockout_untouchable", "UNTOUCHABLE", "Win 5 Knockout Pools",                           koWins,  5,  "knockout", 100000),
  );

  // 8. LEADERBOARD — daily and monthly rank badges
  // Use best rank across all sports (lowest number = best)
  const allDailyDraftRanks    = Object.values(bestDailyDraft).filter(Number.isFinite);
  const allMonthlyDraftRanks  = Object.values(bestMoDraft).filter(Number.isFinite);
  const bestDailyRank         = allDailyDraftRanks.length   ? Math.min(...allDailyDraftRanks)  : null;
  const bestMonthlyRank       = allMonthlyDraftRanks.length ? Math.min(...allMonthlyDraftRanks): null;

  all.push(
    rankBadge("daily_top100",   "ON THE BOARD",    "Top 100 daily leaderboard",  bestDailyRank,   100, "leaderboard", 1000),
    rankBadge("daily_top10",    "HOT HAND",        "Top 10 daily leaderboard",   bestDailyRank,   10,  "leaderboard", 5000),
    rankBadge("daily_top5",     "ALL STAR",        "Top 5 daily leaderboard",    bestDailyRank,   5,   "leaderboard", 10000),
    rankBadge("daily_top1",     "KING FOR A DAY",  "#1 daily leaderboard",       bestDailyRank,   1,   "leaderboard", 15000),
    rankBadge("monthly_top100", "ON THE BOARD",    "Top 100 monthly leaderboard",bestMonthlyRank, 100, "leaderboard", 5000),
    rankBadge("monthly_top10",  "CONTENDER",       "Top 10 monthly leaderboard", bestMonthlyRank, 10,  "leaderboard", 10000),
    rankBadge("monthly_top5",   "ELITE COMPANY",   "Top 5 monthly leaderboard",  bestMonthlyRank, 5,   "leaderboard", 15000),
    rankBadge("monthly_top1",   "CROWN 'EM",       "#1 monthly leaderboard",     bestMonthlyRank, 1,   "leaderboard", 25000),
  );

  // 9. ACHIEVEMENTS — based on count of all above
  const unlockedSoFar = all.filter(b => b.isUnlocked).length;
  all.push(
    badge("achievement_show_off",      "SHOW OFF",       "Earn 25 badges",    unlockedSoFar, 25,  "achievements", 5000),
    badge("achievement_high_performer","50 BURGER",      "Earn 50 badges",    unlockedSoFar, 50,  "achievements", 10000),
    badge("achievement_completionist", "HIGH PERFORMER", "Earn 75 badges",    unlockedSoFar, 75,  "achievements", 15000),
    badge("achievement_big_dipper",    "THE BIG DIPPER", "Earn your 100th badge", unlockedSoFar, 99, "achievements", 100000),
  );

  return all;
}

export function computeUnlockedCount(stats) {
  return computeAllBadges(stats).filter(b => b.isUnlocked).length;
}
